// Small hash router. Routes: #/ , #/t/<tenant>/<page>,
// #/t/<tenant>/<page>?ns=<namespace>&type=<type>&repo=<rep_id>&module=<gom_id>.

export const TENANT_PAGES = [
  "overview",
  "namespaces",
  "trust-configs",
  "principals",
  "grants",
  "members",
  "packages",
  "connect",
  "audit",
  "settings",
] as const;

export type TenantPage = (typeof TENANT_PAGES)[number];

/**
 * Package type in a route: the filter of the Packages page (no type is "All"), or the tab of the Connect page.
 * npm has no route until npm ships (ADR-0035 section 11, ADR-0037).
 */
export const ROUTE_TYPES = ["cargo", "oci", "go"] as const;
export type RouteType = (typeof ROUTE_TYPES)[number];

/**
 * `ns` is the namespace in context, for example from a link on the Namespaces page. `repo` is the `rep_` ID of the
 * OCI repository that the Packages page shows (image detail). `module` is the `gom_` ID of a Go module (module detail).
 */
export type Route =
  | { name: "tenants" }
  | {
      name: "tenant";
      tenant: string;
      page: TenantPage;
      ns?: string;
      type?: RouteType;
      repo?: string;
      module?: string;
    };

/** Parses a location hash into a route. Unknown hashes go to the tenant list. */
export function parseRoute(hash: string): Route {
  const raw = hash.replace(/^#\/?/, "");
  const q = raw.indexOf("?");
  const path = q < 0 ? raw : raw.slice(0, q);
  const parts = path.split("/").filter(Boolean).map(decodeURIComponent);
  if (parts[0] === "t" && parts[1]) {
    const params = new URLSearchParams(q < 0 ? "" : raw.slice(q + 1));
    let page = parts[2] ?? "overview";
    let type = params.get("type");
    // Old links to the Crates page open the Packages page filtered to crates.
    if (page === "crates") {
      page = "packages";
      type = "cargo";
    }
    // #/t/<tenant> opens the overview. Old links to a page (for example #/t/acme/namespaces) still work.
    if ((TENANT_PAGES as readonly string[]).includes(page)) {
      const route: Route = { name: "tenant", tenant: parts[1], page: page as TenantPage };
      const ns = params.get("ns");
      if (ns) route.ns = ns;
      if (type && (ROUTE_TYPES as readonly string[]).includes(type)) route.type = type as RouteType;
      const repo = params.get("repo");
      if (route.page === "packages" && repo && /^rep_[0-9A-Za-z]+$/.test(repo)) route.repo = repo;
      const mod = params.get("module");
      if (route.page === "packages" && mod && /^gom_[0-9A-Za-z]+$/.test(mod)) route.module = mod;
      return route;
    }
  }
  return { name: "tenants" };
}

/** Builds the hash for a route. */
export function routeHash(route: Route): string {
  if (route.name === "tenants") return "#/";
  const base = `#/t/${encodeURIComponent(route.tenant)}/${route.page}`;
  const params = new URLSearchParams();
  if (route.ns) params.set("ns", route.ns);
  if (route.type) params.set("type", route.type);
  if (route.repo) params.set("repo", route.repo);
  if (route.module) params.set("module", route.module);
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}
